import { MAX_IMAGE_FILE_SIZE } from '@/app/constants';
import type { ImageResolution } from '@/app/types';
import { getImageSize } from '../image-utils';
import { detectImageType } from './misc';
import { checkImageDimension, checkImageMagicByte, checkImageMime, checkImageSize } from './validate-image-file-pure';

/**
 * 图片校验失败的结构化原因。
 *
 * | reason              | 说明                                                           |
 * |---------------------|----------------------------------------------------------------|
 * | invalidType         | MIME 不在白名单（非 jpeg/png/webp）                            |
 * | incorrectExtension  | 白名单 MIME 但 magic byte 解析出的实际格式不符                 |
 * | invalidSize         | 文件字节数超过 maxFileSize                                     |
 * | invalidArea         | 解析后宽高乘积小于 MIN_IMAGE_AREA                              |
 * | invalidRatio        | 宽高比超过 MAX_IMAGE_RATIO（任一边）                           |
 * | invalidImage        | 浏览器无法解码（Image.onerror 命中）                           |
 * | 自定义 reason       | `validateResolution` 钩子返回的字符串（工具侧 4K 限制等）      |
 */
export type ImageValidationReason =
  | 'invalidType'
  | 'incorrectExtension'
  | 'invalidSize'
  | 'invalidArea'
  | 'invalidRatio'
  | 'invalidImage'
  | (string & {});

export type ImageValidationResult =
  | { valid: true; imageURL: string; imageResolution: ImageResolution }
  | { valid: false; reason: ImageValidationReason };

export interface ValidateImageFileOptions {
  /**
   * 单文件最大字节数。默认 50MB（与 `MAX_IMAGE_FILE_SIZE` 保持一致）。
   */
  maxFileSize?: number;
  /**
   * 工具侧的额外分辨率校验（如 4K 限制）。返回非 null 时即视为失败。
   */
  validateResolution?: (imageResolution: ImageResolution) => ImageValidationReason | null | undefined;
}

const DEFAULT_MAX_FILE_SIZE = MAX_IMAGE_FILE_SIZE;

/**
 * 校验单张图片是否符合 vidu-next 工具侧 / claw 共享的上传约束。
 *
 * 设计要点：
 * - 不依赖 React、不依赖 next-intl，纯函数 + 浏览器 API，可在任意 client 上下文复用。
 * - 失败时只返回结构化 `reason`，文案由 {@link getImageValidationErrorMessage} 翻译。
 * - 成功时返回已 `createObjectURL` 出来的 `imageURL`，**调用方负责释放**；失败分支内部自行 revoke 临时 URL。
 */
export async function validateImageFile(
  file: File,
  options: ValidateImageFileOptions = {},
): Promise<ImageValidationResult> {
  const maxFileSize = options.maxFileSize ?? DEFAULT_MAX_FILE_SIZE;

  // 1. MIME 白名单
  const mimeReason = checkImageMime(file.type);
  if (mimeReason) {
    return { valid: false, reason: mimeReason };
  }

  // 2. magic byte 校验：白名单 MIME 但实际格式不符
  const detected = await detectImageType(file);
  const magicByteReason = checkImageMagicByte(detected);
  if (magicByteReason) {
    return { valid: false, reason: magicByteReason };
  }

  // 3. 大小
  const sizeReason = checkImageSize(file.size, maxFileSize);
  if (sizeReason) {
    return { valid: false, reason: sizeReason };
  }

  // 4. 解码 + 尺寸 / 比例 / 自定义分辨率
  const imageURL = URL.createObjectURL(file);
  try {
    const { width, height } = await getImageSize(imageURL);

    const dimensionReason = checkImageDimension(width, height);
    if (dimensionReason) {
      URL.revokeObjectURL(imageURL);
      return { valid: false, reason: dimensionReason };
    }

    const imageResolution: ImageResolution = { width, height };
    const customReason = options.validateResolution?.(imageResolution);
    if (customReason) {
      URL.revokeObjectURL(imageURL);
      return { valid: false, reason: customReason };
    }

    return { valid: true, imageURL, imageResolution };
  } catch {
    URL.revokeObjectURL(imageURL);
    return { valid: false, reason: 'invalidImage' };
  }
}
